import { type WabunkaError, WabunkaError_Source } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';

import { WabunkaAPIError } from '@/models/error';
import { useBaseSentry } from '@/wabunka-src/common/composables/general/useSentry/useSentry';
import { buildFetchKey } from '@/wabunka-src/common/utils/buildFetchKey';

import type { AsyncDataOptions } from 'nuxt/app';

export interface UseFetchToPublicWabunkaApiArgs<T> {
  path: Ref<string>; // データ取得先（例：'/experiences'）
  key?: string; // path / method / body が同じでもキャッシュを分けたいときの追加識別子
  options?: AsyncDataOptions<T>; // useFetch のオプションを指定したいときに使用
  method?: Ref<'POST' | 'DELETE' | 'PUT'>;
  body?: Ref<object | undefined>; // API リクエスト用body
  converter: (res: unknown) => T; // json レスポンスに型を付けて返却するコンバーター（基本的に ts-proto が自動生成する fromJson 関数を渡せば OK ）
  additionalHeaders?: Record<string, string>; // 追加のカスタムヘッダー
}

/**
 * ヘッダーの組み立てをおこなう
 * @param path - APIエンドポイントのパス
 * @param additionalHeaders - 追加のカスタムヘッダー
 * @returns ヘッダー用オブジェクト
 */
const buildHeader = (additionalHeaders?: Record<string, string>) => {
  const headers: Record<string, string> = {
    // eslint-disable-next-line
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // eslint-disable-next-line
    'X-Wabunka-Language': 'en',
  };

  // 追加のカスタムヘッダーがあれば適用
  if (additionalHeaders) {
    Object.assign(headers, additionalHeaders);
  }

  return headers;
};

/**
 * Public Wabunka API へデータの更新を行う useFetch ラッパー Composable
 * ページ層では直接使用せず、ドメイン知識を有した Composable を介して利用してください
 *
 * @param useFetchToPublicWabunkaApiArgs
 * @return UseFetch と同じ返り値の型
 */
export const useFetchToPublicWabunkaApi = async <T>({ path, key: customKey, options, converter, method, body, additionalHeaders }: UseFetchToPublicWabunkaApiArgs<T>) => {
  const runtimeConfig = useRuntimeConfig();
  const { captureWabunkaException, captureWabunkaWarning } = useBaseSentry();

  // Nuxt 4 では同じ key の useFetch が data/error/status を共有し、1 回目のリクエストを使い回す。
  const fetchKey = buildFetchKey(path.value, undefined, customKey, { method: method?.value, body: body?.value });

  const { data, pending, error, execute, refresh, status } = await useFetch(path, {
    key: fetchKey,
    method: method,
    body: body,
    ...options,
    baseURL: runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string,
    onRequest({ options }) {
      options.headers = new Headers(buildHeader(additionalHeaders));
    },
  });

  const convertedError = computed<WabunkaAPIError | null>(() => {
    if (error.value == null) {
      return null;
    }
    try {
      const data = error.value.data as WabunkaError;
      return new WabunkaAPIError(data);
    } catch (e) {
      // エラーインスタンス作成に失敗した場合、APIのエラーレスポンスをSentryに送信する
      // ユーザーのネットワーク起因（no response / TypeError: Load failed）は warning に落とす
      const errorMessage = error.value.message ?? '';
      const isNetworkError = errorMessage.includes('<no response>') || errorMessage.includes('TypeError: Load failed');
      const sentryMessage = `Error: "${errorMessage}" for ${path.value}`;
      if (isNetworkError) {
        captureWabunkaWarning(sentryMessage);
      } else {
        captureWabunkaException(new Error(sentryMessage));
      }
      return new WabunkaAPIError({
        status: 500,
        message: `Unexpected error occurred for ${path.value}`,
        source: WabunkaError_Source.WABUNKA,
      } as WabunkaError);
    }
  });

  const convertedData = computed<T | undefined>(() => {
    if (!data.value) {
      return;
    }
    return converter(data.value);
  });

  return { data: convertedData, pending, error: convertedError, execute, refresh, status };
};
